import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { X, ChevronLeft, ChevronRight, ZoomOut } from "lucide-react";

const PHOTOS = [
  "/meraviglia/aldar1.png",
  "/meraviglia/aldar2.png",
  "/meraviglia/evo1.png",
  "/meraviglia/evo2.png",
  "/meraviglia/filarmonica.png",
  "/meraviglia/harpa1.png",
  "/meraviglia/harpa2.png",
  "/meraviglia/magnolias.png",
  "/meraviglia/parakeets.png",
  "/meraviglia/bilbao.png",
  "/meraviglia/beijing1.png",
  "/meraviglia/beijing2.png",
  "/meraviglia/sage.png",
  "/meraviglia/dogwood.png",
  "/meraviglia/drophead.png",
  "/meraviglia/lotus.png",
  "/meraviglia/pony_wisteria.png",
  "/meraviglia/peacock.png",
  "/meraviglia/pony_appleblossom.png",
  "/meraviglia/mv1.png",
  "/meraviglia/mv2.png",
  "/meraviglia/mv3.png",
  "/meraviglia/mv4.png",
  "/meraviglia/mv5.png",
  "/meraviglia/mv6.png",
  "/meraviglia/mv7.png",
];

export default function MeravigliaDelVetro() {
  const [index, setIndex] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const isOpen = index !== null;

  const close = useCallback(() => { setZoomed(false); setIndex(null); }, []);
  const prev = useCallback((e) => { e && e.stopPropagation(); setZoomed(false); setIndex((i) => (i - 1 + PHOTOS.length) % PHOTOS.length); }, []);
  const next = useCallback((e) => { e && e.stopPropagation(); setZoomed(false); setIndex((i) => (i + 1) % PHOTOS.length); }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + PHOTOS.length) % PHOTOS.length);
      else if (e.key === "ArrowRight") setIndex((i) => (i + 1) % PHOTOS.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, close]);

  return (
    <div className="bg-black min-h-screen" data-testid="meraviglia-page">
      <Seo
        title="Meraviglia del Vetro | VETRO DESIGN ITALIA"
        description="Galleria di immagini delle realizzazioni in vetro di VETRO DESIGN ITALIA."
        path="/meraviglia-del-vetro"
      />
      <Navbar />

      {/* HERO */}
      <section
        data-testid="meraviglia-hero"
        className="bg-[#0a0a0a] pt-40 pb-24 md:pt-48 md:pb-32 flex items-center justify-center"
      >
        <h1
          data-testid="meraviglia-title"
          className="font-serif font-light text-white text-center text-5xl sm:text-6xl lg:text-7xl tracking-tight"
        >
          Meraviglia del Vetro
        </h1>
      </section>

      {/* GALLERIA MASONRY */}
      <section data-testid="meraviglia-gallery" className="bg-black px-3 md:px-6 pb-16">
        <div className="columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-4 [column-fill:_balance]">
          {PHOTOS.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              data-testid={`gallery-item-${i}`}
              loading="lazy"
              onClick={() => setIndex(i)}
              className="mb-3 md:mb-4 w-full h-auto block break-inside-avoid cursor-pointer hover:opacity-90 transition-opacity"
            />
          ))}
        </div>
      </section>

      {/* LIGHTBOX */}
      {isOpen && (
        <div
          data-testid="lightbox-overlay"
          onClick={close}
          className="fixed inset-0 z-[100] bg-black flex items-center justify-center overflow-auto"
        >
          <button
            data-testid="lightbox-close"
            onClick={(e) => { e.stopPropagation(); close(); }}
            aria-label="Chiudi"
            className="fixed top-5 right-5 text-white/80 hover:text-white p-2 z-[110]"
          >
            <X className="w-8 h-8" strokeWidth={1.5} />
          </button>
          {zoomed && (
            <button
              data-testid="lightbox-zoomout"
              onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
              aria-label="Riduci"
              className="fixed top-5 right-20 text-white/80 hover:text-white p-2 z-[110]"
            >
              <ZoomOut className="w-8 h-8" strokeWidth={1.5} />
            </button>
          )}
          <button
            data-testid="lightbox-prev"
            onClick={prev}
            aria-label="Precedente"
            className="fixed left-3 md:left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 z-[110]"
          >
            <ChevronLeft className="w-9 h-9 md:w-12 md:h-12" strokeWidth={1.5} />
          </button>
          <img
            src={PHOTOS[index]}
            alt=""
            data-testid="lightbox-image"
            onClick={(e) => { e.stopPropagation(); setZoomed(true); }}
            onDoubleClick={(e) => { e.stopPropagation(); setZoomed(false); }}
            className={`object-contain transition-transform duration-300 ${zoomed ? "max-w-none max-h-none w-auto h-auto scale-[1.8] cursor-zoom-out my-10" : "max-w-[90vw] max-h-[88vh] w-auto h-auto cursor-zoom-in"}`}
          />
          <button
            data-testid="lightbox-next"
            onClick={next}
            aria-label="Successiva"
            className="fixed right-3 md:right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 z-[110]"
          >
            <ChevronRight className="w-9 h-9 md:w-12 md:h-12" strokeWidth={1.5} />
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}
